import React, { useEffect, useState, useMemo, useRef } from "react";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";
import { useBoxContext } from "../../../../Contexts/boxData.context.jsx";
import { getTodayEasternYMD } from "../../../../utils/dateUtils.ts";

const PATCH_EVENT = "FBX_NOTE_LIST_PATCH";

export default function NoteList({ height = 305 }) {
  const NOTES_ENDPOINT = "https://www.fbxchange.com/ReactPHP/notes.php";
  const TOKEN_KEY = "noteToken"; // stores ARRAY of {FBXID, NoteID, NoteDate}

  const {
    setNoteBoxID,
    NoteBoxID,
    noteResetCode,
    setNoteResetCode,
    selectedNoteId,
    setSelectedNoteId,
  } = useBoxContext();

  const { activePlayer } = usePlayer();

  const [NoteArray, setNoteArray] = useState([]);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // hover highlight
  const [hoveredNoteId, setHoveredNoteId] = useState(null);

  // refs for auto-scroll
  const listRef = useRef(null);
  const stickyRef = useRef(null);
  const rowRefs = useRef({}); // { [noteId]: HTMLElement }

  const decodeJwtPayload = (token) => {
    if (!token) return null;
    try {
      return JSON.parse(
        decodeURIComponent(
          atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
        )
      );
    } catch (e) {
      console.error("Error decoding JWT:", e);
      return null;
    }
  };

  const getTodayLocal = () => getTodayEasternYMD();

  // ---------------------------
  // localStorage helpers (ARRAY)
  // ---------------------------
  const readLocalShared = () => {
    try {
      const raw = localStorage.getItem(TOKEN_KEY);
      if (!raw) return [];

      const parsed = JSON.parse(raw);

      // migrate old single-object format -> array
      if (parsed && !Array.isArray(parsed) && typeof parsed === "object") {
        const migrated = [parsed];
        localStorage.setItem(TOKEN_KEY, JSON.stringify(migrated));
        return migrated;
      }

      if (!Array.isArray(parsed)) return [];
      return parsed.filter((x) => x && typeof x === "object");
    } catch (e) {
      console.warn("Failed to parse noteToken:", e);
      return [];
    }
  };

  const writeLocalShared = (nextArray) => {
    try {
      localStorage.setItem(TOKEN_KEY, JSON.stringify(nextArray));
    } catch (e) {
      console.error("Failed to save noteToken:", e);
    }
  };

  // ONLY used on click (per your instructions)
  const upsertLocalForFBXID = (FBXID, NoteID) => {
    if (!FBXID || NoteID == null) return;

    const today = getTodayLocal();
    const prev = readLocalShared();

    const idx = prev.findIndex((r) => String(r?.FBXID) === String(FBXID));
    let next;

    if (idx !== -1) {
      next = prev.map((r, i) =>
        i === idx ? { ...r, FBXID, NoteID, NoteDate: today } : r
      );
    } else {
      next = [...prev, { FBXID, NoteID, NoteDate: today }];
    }

    writeLocalShared(next);
  };

  const meName = useMemo(
    () => String(user?.displayName || "").trim(),
    [user?.displayName]
  );

  // --- robust private detection (covers 1/0, "1"/"0", Y/N, true/false) ---
  const isPrivateNote = (note) => {
    const raw =
      note?.private1 ??
      note?.private ??
      note?.isPrivate ??
      note?.Private ??
      note?.PRIVATE;

    if (raw == null) return false;
    if (typeof raw === "boolean") return raw;

    const s = String(raw).trim().toLowerCase();
    return s === "1" || s === "y" || s === "yes" || s === "true";
  };

  const canViewNote = (note) => {
    const isPrivate = isPrivateNote(note);
    if (!isPrivate) return true;

    const author = String(note?.ByName || "").trim();
    return author && meName && author === meName;
  };

  const recalcPrivateCount = (arr) => {
    if (!Array.isArray(arr)) return 0;
    return arr.reduce((cnt, n) => cnt + (isPrivateNote(n) ? 1 : 0), 0);
  };

  // ✅ compute private count (no setState)
  const privateNotes = useMemo(() => recalcPrivateCount(NoteArray), [NoteArray]);

  const fetchNotes = async () => {
    const token = localStorage.getItem("user2026");
    const payload = decodeJwtPayload(token);
    setUser(payload);

    const scout = payload?.scout ?? null;
    const team = payload?.team ?? null;

    if (!activePlayer?.FBXID || !scout) {
      setIsLoading(false);
      setNoteArray([]);
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(NOTES_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getNotes",
          FBXID: activePlayer.FBXID,
          scout,
          team,
        }),
      });

      const data = await response.json();

      if (!data || Number(data.totalCount) === 0) {
        setNoteArray([]);
        setNoteBoxID(null);
        setSelectedNoteId(null);
        return;
      }

      const notes = Array.isArray(data.notes) ? data.notes : [];
      setNoteArray(notes);
    } catch (error) {
      console.error("Error fetching notes:", error);
      setNoteArray([]);
    } finally {
      setIsLoading(false);
    }
  };

  // reset when player changes
  useEffect(() => {
    setIsLoading(false);
    setNoteArray([]);
    setNoteBoxID(null);
    setSelectedNoteId(null);
    setHoveredNoteId(null);

    rowRefs.current = {};

    const rand = Math.floor(Math.random() * (100000 - 1000 + 1)) + 1000;
    setNoteResetCode(rand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlayer?.FBXID]);

  // reload when reset code changes (player change, create, etc.)
  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteResetCode]);

  // AFTER notes load: select based on localStorage (NO auto-save default)
  useEffect(() => {
    if (!activePlayer?.FBXID) return;

    const allowedNotes = Array.isArray(NoteArray)
      ? NoteArray.filter((n) => canViewNote(n))
      : [];

    if (allowedNotes.length === 0) {
      if (selectedNoteId != null) setSelectedNoteId(null);
      if (NoteBoxID != null) setNoteBoxID(null);
      return;
    }

    const currentOk =
      selectedNoteId != null &&
      allowedNotes.some((n) => String(n?.id) === String(selectedNoteId));

    if (currentOk) {
      if (String(NoteBoxID) !== String(selectedNoteId)) {
        setNoteBoxID(selectedNoteId);
      }
      return;
    }

    const allLocal = readLocalShared();
    const row = allLocal.find(
      (r) => String(r?.FBXID) === String(activePlayer.FBXID)
    );

    if (row && row.NoteID != null) {
      const match = allowedNotes.find(
        (n) => String(n?.id) === String(row.NoteID)
      );
      if (match) {
        setSelectedNoteId(match.id);
        setNoteBoxID(match.id);
        return;
      }
    }

    setSelectedNoteId(allowedNotes[0].id);
    setNoteBoxID(allowedNotes[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [NoteArray, activePlayer?.FBXID, meName]);

  // ✅ AUTO-SCROLL (container-aware + sticky header-safe)
  // - Scrolls DOWN to bottom items when selected.
  // - Scrolls UP to top items if you were previously at bottom.
  useEffect(() => {
    if (!selectedNoteId) return;

    const raf = requestAnimationFrame(() => {
      const container = listRef.current;
      const el = rowRefs.current?.[String(selectedNoteId)];
      if (!container || !el) return;

      const stickyH = stickyRef.current?.offsetHeight || 0;

      // Where the row sits in the scroll container content
      const rowTop = el.offsetTop;
      const rowBottom = rowTop + el.offsetHeight;

      // Visible viewport inside container (accounting for sticky header)
      const viewTop = container.scrollTop + stickyH;
      const viewBottom = container.scrollTop + container.clientHeight;

      // If above visible area, scroll up so row is just under sticky header
      if (rowTop < viewTop) {
        container.scrollTop = Math.max(0, rowTop - stickyH);
        return;
      }

      // If below visible area, scroll down so row bottom is visible
      if (rowBottom > viewBottom) {
        container.scrollTop = Math.max(0, rowBottom - container.clientHeight);
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [selectedNoteId]);

  // patch listener updates NoteArray ONLY
  useEffect(() => {
    const handler = (evt) => {
      const detail = evt?.detail || {};
      const { action, id, patch } = detail;

      if (!id) return;

      if (action === "update") {
        setNoteArray((prev) =>
          prev.map((n) =>
            String(n?.id) === String(id) ? { ...n, ...(patch || {}) } : n
          )
        );
      }

      if (action === "remove") {
        setNoteArray((prev) => prev.filter((n) => String(n?.id) !== String(id)));
      }
    };

    window.addEventListener(PATCH_EVENT, handler);
    return () => window.removeEventListener(PATCH_EVENT, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meName]);

  const handleSelectNote = (note) => {
    if (!note?.id) return;
    if (!activePlayer?.FBXID) return;

    if (String(NoteBoxID) === String(note.id)) return;

    if (!canViewNote(note)) {
      alert("This is a Private Note. You do not have permission to view it.");
      return;
    }

    setSelectedNoteId(note.id);
    setNoteBoxID(note.id);

    upsertLocalForFBXID(activePlayer.FBXID, note.id);
  };

  return (
    <div
      ref={listRef}
      className="w-100"
      style={{
        height,
        overflowY: "auto",
        overflowX: "hidden",
        position: "relative",
      }}
    >
      {/* ✅ STICKY HEADER WRAP (player summary + navy header stay pinned together) */}
      <div
        ref={stickyRef}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          background: "var(--bs-body-bg, #fff)",
          boxShadow: "0 1px 0 rgba(0,0,0,0.08)",
        }}
      >
        <div className="alert-secondary p-1 text-center fs-7 m-0">
          {activePlayer ? activePlayer.last : ""},{" "}
          {activePlayer ? activePlayer.first : ""},{" "}
          {activePlayer ? activePlayer.colPos1 : ""} [
          {activePlayer ? activePlayer.school : ""}]{" "}
          <span className="fw-bold">
            Notes: {Array.isArray(NoteArray) ? NoteArray.length : 0} [{privateNotes} Private]
          </span>
        </div>

        <div
          className="d-flex bg-navy fs-7 fw-normal gap-2 px-2 align-items-center"
          style={{
            background: "var(--navy, #001f3f)",
            height: "35px",
            lineHeight: "35px",
          }}
        >
          <div className="col-8 text-lemon">Date</div>
          <div className="flex-grow-1 text-lemon">Note Title</div>
          <div className="col-8 text-lemon">By</div>
          <div className="col-8 text-lemon">Team</div>
        </div>
      </div>

      {!activePlayer?.FBXID ? (
        <div className="p-2 fs-7 text-muted">
          <em>Select a player below to load notes.</em>
        </div>
      ) : isLoading ? (
        <div className="p-2 fs-7 alert-info fw-bold">Loading notes...</div>
      ) : NoteArray.length === 0 ? (
        <div className="p-2 fs-7 text-danger fw-bold">No notes found</div>
      ) : (
        NoteArray.map((n, i) => {
          const isSelected =
            parseInt(selectedNoteId, 10) === parseInt(n?.id, 10);
          const isHovered = String(hoveredNoteId) === String(n?.id);
          const hoverClass = !isSelected && isHovered ? "alert-light" : "";

          const isPrivate = isPrivateNote(n);
          const author = String(n?.ByName || "").trim();
          const mine = author && meName && author === meName;
          const hidePrivate = isPrivate && !mine;

          return (
            <div
              key={n?.id ?? i}
              ref={(el) => {
                const key = String(n?.id ?? "");
                if (!key) return;
                if (el) rowRefs.current[key] = el;
                else delete rowRefs.current[key];
              }}
              className={`d-flex fs-7 gap-2 px-2 align-items-center border-bottom
                ${isSelected ? "alert-warning" : ""}
                ${hoverClass}
                ${isPrivate ? "text-danger" : ""}
                ${hidePrivate ? "fw-bold" : ""}`}
              style={{
                height: "35px",
                cursor: hidePrivate ? "not-allowed" : "pointer",
                opacity: hidePrivate ? 0.65 : 1,
              }}
              onMouseEnter={() => setHoveredNoteId(n?.id)}
              onMouseLeave={() => setHoveredNoteId(null)}
              onClick={() => handleSelectNote(n)}
            >
              <div className="col-8 text-start">{n?.DateOfContact || ""}</div>

              <div className="flex-grow-1 text-truncate">
                {hidePrivate ? "Private Note" : n?.SourceData || "Untitled"}
              </div>

              <div className="col-8 text-start text-truncate">{author || ""}</div>
              <div className="col-8 text-start text-truncate">
                {n?.VisibleTo || ""}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
